<template>
  <div>
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="secondary"
      class="mb-3"
    />

    <v-alert
      v-if="loadError"
      type="error"
      dense
      outlined
      border="left"
    >
      {{ loadError }}
    </v-alert>

    <template v-if="!loading && !loadError">
      <!-- Current subscription card -->
      <v-sheet
        rounded
        outlined
        class="pa-4 mb-4"
      >
        <div class="d-flex align-center mb-2">
          <v-icon
            :color="currentSubscription ? 'primary' : 'grey'"
            class="mr-2"
          >
            mdi-card-account-details-outline
          </v-icon>
          <div class="text-subtitle-1">
            Assinatura atual
          </div>
          <v-spacer />
          <v-chip
            v-if="currentSubscription"
            :color="statusColor(currentSubscription.status)"
            x-small
            dark
          >
            {{ currentSubscription.status }}
          </v-chip>
          <v-chip
            v-else
            color="grey"
            x-small
            dark
          >
            sem plano
          </v-chip>
        </div>

        <div
          v-if="!currentSubscription"
          class="text-body-2 grey--text"
        >
          Nenhuma assinatura cadastrada para esta organização.
        </div>

        <v-row
          v-else
          dense
        >
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <div class="text-caption grey--text">
              Plano
            </div>
            <div class="text-body-1">
              {{ currentSubscription.plano_nome || '—' }}
              <span class="text-caption grey--text">
                ({{ currentSubscription.plano_codigo || currentSubscription.plano_id }})
              </span>
            </div>
          </v-col>
          <v-col
            cols="6"
            sm="3"
            md="2"
          >
            <div class="text-caption grey--text">
              Vencimento (dia)
            </div>
            <div class="text-body-1">
              {{ currentSubscription.dia_vencimento }}
            </div>
          </v-col>
          <v-col
            cols="6"
            sm="3"
            md="2"
          >
            <div class="text-caption grey--text">
              Cupom
            </div>
            <div class="text-body-1">
              {{ couponLabel(currentSubscription.cupom_id) }}
            </div>
          </v-col>
          <v-col
            cols="6"
            sm="3"
            md="2"
          >
            <div class="text-caption grey--text">
              Início
            </div>
            <div class="text-body-1">
              {{ formatDate(currentSubscription.data_inicio) }}
            </div>
          </v-col>
          <v-col
            cols="6"
            sm="3"
            md="3"
          >
            <div class="text-caption grey--text">
              Fim
            </div>
            <div class="text-body-1">
              {{ currentSubscription.data_fim ? formatDate(currentSubscription.data_fim) : '—' }}
            </div>
          </v-col>
        </v-row>

        <div class="mt-3 text-right">
          <v-btn
            v-if="currentSubscription && !isTerminal(currentSubscription.status)"
            color="warning"
            small
            text
            :loading="acting"
            @click="cancel(currentSubscription)"
          >
            <v-icon
              left
              small
            >
              mdi-cancel
            </v-icon>
            Cancelar
          </v-btn>
          <v-btn
            v-if="currentSubscription && isTerminal(currentSubscription.status)"
            color="success"
            small
            text
            :loading="acting"
            @click="reactivate(currentSubscription)"
          >
            <v-icon
              left
              small
            >
              mdi-restart
            </v-icon>
            Reativar
          </v-btn>
          <v-btn
            v-if="currentSubscription && !isTerminal(currentSubscription.status)"
            color="primary"
            small
            class="ml-2"
            @click="openEditDialog(currentSubscription)"
          >
            <v-icon
              left
              small
            >
              mdi-pencil
            </v-icon>
            Editar
          </v-btn>
          <v-btn
            color="secondary"
            small
            class="ml-2"
            @click="openCreateDialog"
          >
            <v-icon
              left
              small
            >
              mdi-plus
            </v-icon>
            Nova assinatura
          </v-btn>
        </div>
      </v-sheet>

      <!-- History table -->
      <v-sheet
        rounded
        outlined
        class="pa-4"
      >
        <div class="d-flex align-center mb-2">
          <v-icon
            color="grey darken-2"
            class="mr-2"
          >
            mdi-history
          </v-icon>
          <div class="text-subtitle-1">
            Histórico
          </div>
          <v-spacer />
          <v-btn
            small
            icon
            @click="load"
          >
            <v-icon small>
              mdi-refresh
            </v-icon>
          </v-btn>
        </div>
        <v-data-table
          :headers="headers"
          :items="subscriptions"
          item-key="id"
          dense
          :items-per-page="10"
          :footer-props="{ 'items-per-page-options': [5, 10, 25] }"
        >
          <template #item.status="{ item }">
            <v-chip
              :color="statusColor(item.status)"
              x-small
              dark
            >
              {{ item.status }}
            </v-chip>
          </template>
          <template #item.plano="{ item }">
            {{ item.plano_nome || '—' }}
            <span class="text-caption grey--text">
              ({{ item.plano_codigo || item.plano_id }})
            </span>
          </template>
          <template #item.cupom_id="{ item }">
            {{ couponLabel(item.cupom_id) }}
          </template>
          <template #item.data_inicio="{ item }">
            {{ formatDate(item.data_inicio) }}
          </template>
          <template #item.data_fim="{ item }">
            {{ item.data_fim ? formatDate(item.data_fim) : '—' }}
          </template>
        </v-data-table>
      </v-sheet>
    </template>

    <!-- Create / edit dialog -->
    <v-dialog
      v-model="dialog.show"
      max-width="520"
      persistent
    >
      <v-card>
        <v-card-title>
          {{ dialog.mode === 'create' ? 'Nova assinatura' : 'Editar assinatura' }}
        </v-card-title>
        <v-card-text>
          <v-select
            v-model="dialog.form.plano_id"
            :items="planOptions"
            label="Plano"
            outlined
            dense
            :disabled="dialog.mode === 'edit'"
            :hint="dialog.mode === 'edit' ? 'Pode trocar o plano da assinatura' : ''"
            persistent-hint
          />
          <v-select
            v-model="dialog.form.plano_id"
            :items="planOptions"
            label="Plano (editar)"
            outlined
            dense
            :class="{ 'd-none': dialog.mode !== 'edit' }"
          />
          <v-select
            v-model="dialog.form.cupom_id"
            :items="couponOptions"
            label="Cupom"
            outlined
            dense
            clearable
            class="mt-2"
          />
          <v-text-field
            v-model.number="dialog.form.dia_vencimento"
            label="Dia do vencimento (1-28)"
            type="number"
            min="1"
            max="28"
            outlined
            dense
            class="mt-2"
          />
          <v-alert
            v-if="dialog.error"
            type="error"
            dense
            outlined
            border="left"
            class="mt-2"
          >
            {{ dialog.error }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="dialog.saving"
            @click="dialog.show = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="secondary"
            :loading="dialog.saving"
            @click="submitDialog"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
  import moment from 'moment'
  import { $k } from '@/lib/kraken'

  const TERMINAL_STATUS = new Set(['cancelada', 'expirada'])

  export default {
    name: 'SubscriptionsTab',

    props: {
      org: { type: Object, required: true },
    },

    data: () => ({
      loading: false,
      loadError: '',
      acting: false,
      subscriptions: [],
      plans: [],
      coupons: [],
      headers: [
        { text: 'ID', value: 'id', width: 80 },
        { text: 'Status', value: 'status', width: 130 },
        { text: 'Plano', value: 'plano' },
        { text: 'Cupom', value: 'cupom_id', width: 160 },
        { text: 'Início', value: 'data_inicio', width: 120 },
        { text: 'Fim', value: 'data_fim', width: 120 },
        { text: 'Vencimento', value: 'dia_vencimento', width: 110 },
      ],
      dialog: {
        show: false,
        mode: 'create',
        saving: false,
        error: '',
        editingId: null,
        form: { plano_id: null, cupom_id: null, dia_vencimento: 10 },
      },
      snackbar: { show: false, color: 'success', text: '' },
    }),

    computed: {
      currentSubscription () {
        // First non-terminal in date-desc order, otherwise the most recent row
        return this.subscriptions.find(s => !TERMINAL_STATUS.has(s.status)) || this.subscriptions[0] || null
      },
      planOptions () {
        return this.plans.map(p => ({
          text: `${p.nome} (${p.codigo_plano})`,
          value: p.id,
        }))
      },
      couponOptions () {
        return this.coupons.map(c => ({
          text: `${c.codigo} — ${c.efeito} ${c.valor}`,
          value: c.id,
        }))
      },
    },

    watch: {
      'org.id': {
        immediate: true,
        handler () { this.load() },
      },
    },

    methods: {
      formatDate (v) {
        if (!v) return '—'
        const m = moment(v)
        return m.isValid() ? m.format('DD/MM/YYYY') : v
      },
      statusColor (status) {
        switch (status) {
          case 'ativa': return 'success'
          case 'teste': return 'info'
          case 'inadimplente': return 'warning'
          case 'cancelada': return 'grey'
          case 'expirada': return 'error'
          default: return 'grey'
        }
      },
      isTerminal (status) {
        return TERMINAL_STATUS.has(status)
      },
      couponLabel (id) {
        if (!id) return '—'
        const c = this.coupons.find(x => x.id === id)
        return c ? c.codigo : `#${id}`
      },
      msgFromError (err, fallback) {
        return err?.response?.data?.messages?.error?.[0] ||
          err?.response?.data?.messages?.[0]?.text ||
          err?.message ||
          fallback
      },
      async load () {
        if (!this.org?.id) return
        this.loading = true
        this.loadError = ''
        try {
          const [subs, plans, coupons] = await Promise.all([
            $k.manage.clinic_one.subscriptions.organization._idOrganizacao.get({ idOrganizacao: this.org.id }),
            $k.manage.clinic_one.plans.get({ limit: 200 }),
            $k.manage.clinic_one.coupons.get({ limit: 200, vigentes: true }),
          ])
          this.subscriptions = subs.results || []
          this.plans = plans.results || []
          this.coupons = coupons.results || []
        } catch (err) {
          console.error('[subscriptions.load]', err)
          this.loadError = this.msgFromError(err, 'Falha ao carregar assinaturas.')
        } finally {
          this.loading = false
        }
      },
      openCreateDialog () {
        this.dialog = {
          show: true,
          mode: 'create',
          saving: false,
          error: '',
          editingId: null,
          form: { plano_id: this.plans[0]?.id || null, cupom_id: null, dia_vencimento: 10 },
        }
      },
      openEditDialog (sub) {
        this.dialog = {
          show: true,
          mode: 'edit',
          saving: false,
          error: '',
          editingId: sub.id,
          form: {
            plano_id: sub.plano_id,
            cupom_id: sub.cupom_id,
            dia_vencimento: sub.dia_vencimento,
          },
        }
      },
      async submitDialog () {
        const { mode, form, editingId } = this.dialog
        if (!form.plano_id || !form.dia_vencimento) {
          this.dialog.error = 'Plano e dia de vencimento são obrigatórios.'
          return
        }
        this.dialog.saving = true
        this.dialog.error = ''
        try {
          if (mode === 'create') {
            await $k.manage.clinic_one.subscriptions.post({
              id_organizacao: this.org.id,
              plano_id: form.plano_id,
              cupom_id: form.cupom_id || null,
              dia_vencimento: form.dia_vencimento,
            })
            this.snackbar = { show: true, color: 'success', text: 'Assinatura criada.' }
          } else {
            await $k.manage.clinic_one.subscriptions._id.put({
              id: editingId,
              plano_id: form.plano_id,
              cupom_id: form.cupom_id || null,
              dia_vencimento: form.dia_vencimento,
            })
            this.snackbar = { show: true, color: 'success', text: 'Assinatura atualizada.' }
          }
          this.dialog.show = false
          await this.load()
        } catch (err) {
          console.error('[subscriptions.submit]', err)
          this.dialog.error = this.msgFromError(err, 'Falha ao salvar.')
        } finally {
          this.dialog.saving = false
        }
      },
      async cancel (sub) {
        if (!confirm(`Cancelar a assinatura #${sub.id}?`)) return
        this.acting = true
        try {
          await $k.manage.clinic_one.subscriptions._id.cancel.patch({ id: sub.id })
          this.snackbar = { show: true, color: 'success', text: 'Assinatura cancelada.' }
          await this.load()
        } catch (err) {
          console.error('[subscriptions.cancel]', err)
          this.snackbar = { show: true, color: 'error', text: this.msgFromError(err, 'Falha ao cancelar.') }
        } finally {
          this.acting = false
        }
      },
      async reactivate (sub) {
        this.acting = true
        try {
          await $k.manage.clinic_one.subscriptions._id.reactivate.patch({ id: sub.id })
          this.snackbar = { show: true, color: 'success', text: 'Assinatura reativada.' }
          await this.load()
        } catch (err) {
          console.error('[subscriptions.reactivate]', err)
          this.snackbar = { show: true, color: 'error', text: this.msgFromError(err, 'Falha ao reativar.') }
        } finally {
          this.acting = false
        }
      },
    },
  }
</script>
